import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Eye, Edit2, Trash2, Search, X, User, Users, UsersRound, Download, FileSpreadsheet, Upload, AlertTriangle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function EntriesTab({ entries, setEntries, fetchEntries, skaters, fetchSkaters, competitions, grades, disciplines, clubs = [], currentUser }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateCompetitionId, setTemplateCompetitionId] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  // ``last regenerated`` timestamps for each variant of the entry
  // template cache. Refreshed when the dialog opens and after every
  // download. Used to show admins when the workbook last refreshed.
  const [templateCacheMeta, setTemplateCacheMeta] = useState({ blank: null, populated: null });
  const [templatePopulated, setTemplatePopulated] = useState(false);
  // Club filter for the "Current entry list" download. Empty string =
  // "All Clubs". Holds the club_id when a specific club is chosen.
  const [templateClubFilter, setTemplateClubFilter] = useState('');
  // Clubs that have at least one entry in the selected competition,
  // populated when the dialog opens / the competition changes.
  const [templateClubsWithEntries, setTemplateClubsWithEntries] = useState([]);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importCompetitionId, setImportCompetitionId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [currentConflict, setCurrentConflict] = useState(null);
  const [conflictQueue, setConflictQueue] = useState([]);
  
  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteCompetitionId, setBulkDeleteCompetitionId] = useState('all');
  const [competitionCounts, setCompetitionCounts] = useState({ competitions: [], total_entries: 0 });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Schedule grades for filtering
  const [scheduleGrades, setScheduleGrades] = useState([]);
  
  // Filters
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [skaterFilter, setSkaterFilter] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    competition_id: '',
    entry_type: 'individual',
    skater1_id: '',
    skater2_id: '',
    team_name: '',
    managing_club_id: '',
    team_skater_ids: [],
    team_reserve_ids: [],  // Reserve skaters for teams
    // 6 discipline/grade pairs for individual, 3 for pairs.
    // When editing, each row also carries `entry_id` so save can PATCH it
    // in place rather than creating a duplicate.
    entries: [
      { discipline_id: '', grade_id: '' },
      { discipline_id: '', grade_id: '' },
      { discipline_id: '', grade_id: '' },
      { discipline_id: '', grade_id: '' },
      { discipline_id: '', grade_id: '' },
      { discipline_id: '', grade_id: '' },
    ]
  });
  
  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;
  
  const canEditItem = (item) => {
    return isSuperUser || item.created_by === currentUserId || item.can_edit === true;
  };
  
  // Check if a competition is past its closing date + 3 day buffer
  const isCompetitionPastClosingDate = (competitionId) => {
    const comp = competitions.find(c => c.competition_id === competitionId);
    if (!comp?.registration_deadline) return false;
    
    const closingDate = new Date(comp.registration_deadline);
    const bufferDate = new Date(closingDate);
    bufferDate.setDate(bufferDate.getDate() + 3); // 3 day buffer
    
    return new Date() > bufferDate;
  };
  
  // Check if standard user can upload for a competition
  const canUploadEntries = (competitionId) => {
    if (isSuperUser) return true;
    return !isCompetitionPastClosingDate(competitionId);
  };
  
  // Fetch competition entry counts for bulk delete dialog
  const fetchCompetitionCounts = async () => {
    try {
      const res = await axios.get(`${API}/entries/competition-counts`, { withCredentials: true });
      setCompetitionCounts(res.data);
    } catch (error) {
      console.error('Error fetching competition counts:', error);
    }
  };
  
  // Get display name for bulk delete confirmation
  const getBulkDeleteTargetName = () => {
    if (bulkDeleteCompetitionId === 'all') {
      return 'ALL competitions';
    }
    const comp = competitionCounts.competitions.find(c => c.competition_id === bulkDeleteCompetitionId);
    return comp ? comp.name : 'selected competition';
  };
  
  // Get entry count for selected bulk delete target
  const getBulkDeleteEntryCount = () => {
    if (bulkDeleteCompetitionId === 'all') {
      return competitionCounts.total_entries;
    }
    const comp = competitionCounts.competitions.find(c => c.competition_id === bulkDeleteCompetitionId);
    return comp ? comp.entry_count : 0;
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const params = bulkDeleteCompetitionId === 'all' ? {} : { competition_id: bulkDeleteCompetitionId };
      const res = await axios.delete(`${API}/entries/bulk-delete`, { 
        params,
        withCredentials: true 
      });
      
      toast.success(res.data.message);
      setBulkDeleteConfirmOpen(false);
      setBulkDeleteDialogOpen(false);
      setBulkDeleteCompetitionId('all');
      fetchEntries();
      fetchSkaters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Open bulk delete dialog
  const openBulkDeleteDialog = () => {
    fetchCompetitionCounts();
    setBulkDeleteCompetitionId('all');
    setBulkDeleteDialogOpen(true);
  };
  
  // Fetch schedule grades when competition changes
  useEffect(() => {
    const fetchScheduleGrades = async () => {
      if (!formData.competition_id) {
        setScheduleGrades([]);
        return;
      }
      
      try {
        // Fetch available grades for this competition
        const res = await axios.get(`${API}/competitions/${formData.competition_id}/available-grades`, { withCredentials: true });
        
        // Combine main and sub schedule grades
        const allGrades = [
          ...(res.data.main_schedule_grades || []),
          ...(res.data.sub_schedule_grades || [])
        ];
        
        // Format for display: "Grade Discipline" eg "Senior Solo Dance"
        const formattedGrades = allGrades.map(g => ({
          ...g,
          discipline_id: g.discipline_id,
          discipline_name: g.discipline_name || '',
          display_name: `${g.name || ''} ${g.discipline_name || ''}`.trim()
        }));
        
        setScheduleGrades(formattedGrades);
      } catch (error) {
        console.error('Error fetching available grades:', error);
        setScheduleGrades([]);
      }
    };
    
    fetchScheduleGrades();
  }, [formData.competition_id]);
  
  // Get disciplines available in the schedule (for filtering)
  const getAvailableDisciplines = () => {
    // Extract unique disciplines from schedule grades
    const disciplineMap = new Map();
    scheduleGrades.forEach(sg => {
      if (sg.discipline_id && !disciplineMap.has(sg.discipline_id)) {
        disciplineMap.set(sg.discipline_id, {
          discipline_id: sg.discipline_id,
          name: sg.discipline_name || 'Unknown',
          competitor_type: sg.competitor_type || 'Individual'
        });
      }
    });
    return Array.from(disciplineMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Get grades available for a specific discipline
  const getGradesForDiscipline = (disciplineId) => {
    if (!disciplineId) return [];
    return scheduleGrades
      .filter(sg => sg.discipline_id === disciplineId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };
  
  // Check if a discipline is a pairs discipline
  const isPairsDiscipline = (discipline) => {
    if (!discipline) return false;
    return discipline.competitor_type === 'Pairs';
  };
  
  // Check if a discipline is a teams discipline
  const isTeamsDiscipline = (discipline) => {
    if (!discipline) return false;
    return discipline.competitor_type === 'Teams';
  };
  
  // Get filtered disciplines based on entry type
  const getFilteredDisciplines = () => {
    const available = getAvailableDisciplines();
    if (formData.entry_type === 'pair') {
      return available.filter(d => isPairsDiscipline(d));
    } else if (formData.entry_type === 'team') {
      return available.filter(d => isTeamsDiscipline(d));
    } else if (formData.entry_type === 'individual') {
      return available.filter(d => !isPairsDiscipline(d) && !isTeamsDiscipline(d));
    }
    return available;
  };
  
  const resetForm = (keepCompetition = false) => {
    setFormData(prev => ({
      competition_id: keepCompetition ? prev.competition_id : '',
      entry_type: 'individual',
      skater1_id: '',
      skater2_id: '',
      team_name: '',
      managing_club_id: '',
      team_skater_ids: [],
      team_reserve_ids: [],
      entries: [
        { discipline_id: '', grade_id: '' },
        { discipline_id: '', grade_id: '' },
        { discipline_id: '', grade_id: '' },
        { discipline_id: '', grade_id: '' },
        { discipline_id: '', grade_id: '' },
        { discipline_id: '', grade_id: '' },
      ]
    }));
    setEditingItem(null);
  };
  
  const updateEntryRow = (index, field, value) => {
    setFormData(prev => {
      const newEntries = [...prev.entries];
      newEntries[index] = { ...newEntries[index], [field]: value };
      // If discipline changes, reset grade
      if (field === 'discipline_id') {
        newEntries[index].grade_id = '';
      }
      return { ...prev, entries: newEntries };
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate skater selection
    if (formData.entry_type === 'individual' && !formData.skater1_id) {
      toast.error('Please select a skater');
      return;
    }
    if (formData.entry_type === 'pair' && (!formData.skater1_id || !formData.skater2_id)) {
      toast.error('Please select both skaters for the pair');
      return;
    }
    // For teams, count non-reserve members (team_skater_ids minus team_reserve_ids)
    if (formData.entry_type === 'team') {
      const nonReserveCount = formData.team_skater_ids.filter(id => !formData.team_reserve_ids.includes(id)).length;
      if (nonReserveCount < 4) {
        toast.error('Please select at least 4 team members (not including reserves)');
        return;
      }
    }
    
    // Get filled entries (have both discipline and grade selected)
    const filledEntries = formData.entries.filter(e => e.discipline_id && e.grade_id);
    
    if (filledEntries.length === 0 && formData.entry_type !== 'team') {
      toast.error('Please select at least one discipline and grade');
      return;
    }
    
    // For teams, we still use single grade selection
    if (formData.entry_type === 'team' && !formData.entries[0].grade_id) {
      toast.error('Please select a grade for the team');
      return;
    }
    
    setSubmitting(true);
    
    try {
      let createdCount = 0;
      const skaterName = skaters.find(s => s.skater_id === formData.skater1_id);
      const displayName = skaterName ? `${skaterName.first_name} ${skaterName.last_name}` : 'skater';
      
      if (formData.entry_type === 'team') {
        // Single entry for team
        await axios.post(`${API}/entries`, {
          competition_id: formData.competition_id,
          entry_type: 'team',
          grade_id: formData.entries[0].grade_id,
          team_name: formData.team_name,
          managing_club_id: formData.managing_club_id || null,
          team_skater_ids: formData.team_skater_ids,
          team_reserve_ids: formData.team_reserve_ids,
          entry_status: 'confirmed'
        }, { withCredentials: true });
        createdCount = 1;
        toast.success(`Entered ${formData.team_name || 'team'} into 1 event`);
      } else {
        // Multiple entries for individual/pair
        for (const entry of filledEntries) {
          await axios.post(`${API}/entries`, {
            competition_id: formData.competition_id,
            entry_type: formData.entry_type,
            grade_id: entry.grade_id,
            team_name: formData.entry_type === 'pair' ? (() => {
              const s1 = skaters.find(s => s.skater_id === formData.skater1_id);
              const s2 = skaters.find(s => s.skater_id === formData.skater2_id);
              return `${s1?.first_name || ''} ${s1?.last_name || ''} & ${s2?.first_name || ''} ${s2?.last_name || ''}`.trim();
            })() : null,
            skater1_id: formData.skater1_id,
            skater2_id: formData.entry_type === 'pair' ? formData.skater2_id : null,
            team_skater_ids: [],
            entry_status: 'confirmed'
          }, { withCredentials: true });
          createdCount++;
        }
        
        if (formData.entry_type === 'pair') {
          const skater2Name = skaters.find(s => s.skater_id === formData.skater2_id);
          const pairName = `${displayName} & ${skater2Name?.first_name || ''} ${skater2Name?.last_name || ''}`;
          toast.success(`Entered ${pairName} into ${createdCount} event${createdCount > 1 ? 's' : ''}`);
        } else {
          toast.success(`Entered ${displayName} into ${createdCount} event${createdCount > 1 ? 's' : ''}`);
        }
      }
      
      fetchEntries();
      // Reset form but keep competition selected
      resetForm(true);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create entries';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleView = (entry) => {
    setViewingItem(entry);
    setViewDialogOpen(true);
  };
  
  /**
   * Bring up the edit dialog showing EVERY event this skater (or pair)
   * is entered in for the current competition — not just the row the
   * user clicked. This lets the organiser fix all of a skater's grades
   * in one place and add/remove additional events alongside.
   *
   * Each row carries its existing ``entry_id`` so save can PATCH it in
   * place; rows added by the user have no entry_id and get POSTed as
   * new entries on save.
   */
  const handleEdit = (entry) => {
    setEditingItem(entry);

    let siblingEntries = [];
    if (entry.entry_type === 'individual') {
      siblingEntries = entries.filter(e =>
        e.entry_type === 'individual' &&
        e.competition_id === entry.competition_id &&
        e.skater1_id === entry.skater1_id
      );
    } else if (entry.entry_type === 'pair') {
      const pairKey = [entry.skater1_id, entry.skater2_id].filter(Boolean).sort().join('|');
      siblingEntries = entries.filter(e => {
        if (e.entry_type !== 'pair' || e.competition_id !== entry.competition_id) return false;
        const otherKey = [e.skater1_id, e.skater2_id].filter(Boolean).sort().join('|');
        return otherKey === pairKey;
      });
    } else {
      // Teams: only the clicked entry (very few teams ever have multiple events).
      siblingEntries = [entry];
    }

    // Map every sibling entry into a form row, preserving entry_id so we
    // can PATCH in place. Sort by entry_number so older rows come first.
    siblingEntries.sort((a, b) => (a.entry_number || 0) - (b.entry_number || 0));
    const rows = siblingEntries.map(e => ({
      entry_id: e.entry_id,
      discipline_id: (grades.find(g => g.grade_id === e.grade_id) || {}).discipline_id || '',
      grade_id: e.grade_id || '',
    }));
    // Always keep one blank row at the end so adding more events is a
    // single click away.
    rows.push({ discipline_id: '', grade_id: '' });

    setFormData({
      competition_id: entry.competition_id || '',
      entry_type: entry.entry_type || 'individual',
      skater1_id: entry.skater1_id || '',
      skater2_id: entry.skater2_id || '',
      team_name: entry.team_name || '',
      managing_club_id: entry.managing_club_id || '',
      team_skater_ids: entry.team_skater_ids || [],
      team_reserve_ids: entry.team_reserve_ids || [],
      entries: rows,
    });
    setDialogOpen(true);
  };

  /**
   * Remove an entry row from the edit dialog. If the row corresponds to
   * an existing entry, DELETE it server-side immediately so the change
   * survives even if the user closes the dialog without clicking Update.
   */
  const removeEntryRow = async (rowIndex) => {
    const row = formData.entries[rowIndex];
    if (row.entry_id) {
      try {
        await axios.delete(`${API}/entries/${row.entry_id}`, { withCredentials: true });
        toast.success('Entry removed');
        fetchEntries();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to remove entry');
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== rowIndex),
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    setSubmitting(true);
    try {
      // Walk every form row. Existing rows get PATCHed if their grade
      // changed; blank rows are ignored; new rows with a grade selected
      // are POSTed as additional entries on the same skater/pair/team.
      const rowsWithGrade = formData.entries.filter(r => r.grade_id);
      const operations = rowsWithGrade.map(async (row) => {
        if (row.entry_id) {
          return axios.patch(`${API}/entries/${row.entry_id}`, {
            grade_id: row.grade_id,
            team_name: formData.team_name,
            managing_club_id: formData.managing_club_id || null,
            skater1_id: formData.skater1_id,
            skater2_id: formData.skater2_id,
            team_skater_ids: formData.team_skater_ids,
            team_reserve_ids: formData.team_reserve_ids,
          }, { withCredentials: true });
        }
        // New row → create a fresh entry for the same skater/pair/team.
        return axios.post(`${API}/entries`, {
          competition_id: formData.competition_id,
          entry_type: formData.entry_type,
          grade_id: row.grade_id,
          team_name: formData.team_name || null,
          managing_club_id: formData.managing_club_id || null,
          skater1_id: formData.skater1_id || null,
          skater2_id: formData.skater2_id || null,
          team_skater_ids: formData.team_skater_ids,
          team_reserve_ids: formData.team_reserve_ids,
        }, { withCredentials: true });
      });
      await Promise.all(operations);
      toast.success('Entries updated');
      fetchEntries();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteClick = (entryId) => {
    setItemToDelete(entryId);
    setDeleteDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/entries/${itemToDelete}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchEntries();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleTeamSkaterToggle = (skaterId) => {
    setFormData(prev => {
      const currentMembers = prev.team_skater_ids || [];
      const currentReserves = prev.team_reserve_ids || [];
      
      if (currentMembers.includes(skaterId)) {
        // Remove from members
        return { 
          ...prev, 
          team_skater_ids: currentMembers.filter(id => id !== skaterId)
        };
      } else {
        // Add to members (and remove from reserves if they were there)
        return { 
          ...prev, 
          team_skater_ids: [...currentMembers, skaterId],
          team_reserve_ids: currentReserves.filter(id => id !== skaterId)
        };
      }
    });
  };
  
  const handleTeamReserveToggle = (skaterId) => {
    setFormData(prev => {
      const currentReserves = prev.team_reserve_ids || [];
      const currentMembers = prev.team_skater_ids || [];
      
      // Can only mark as reserve if they're currently a team member
      if (!currentMembers.includes(skaterId)) {
        return prev;
      }
      
      if (currentReserves.includes(skaterId)) {
        // Remove from reserves (but keep as member)
        return { 
          ...prev, 
          team_reserve_ids: currentReserves.filter(id => id !== skaterId)
        };
      } else {
        // Add to reserves
        return { 
          ...prev, 
          team_reserve_ids: [...currentReserves, skaterId]
        };
      }
    });
  };
  
  // Get club name for a skater
  const getSkaterClubName = (skater) => {
    return skater?.club_name || 'No club';
  };
  
  /** Pull "last regenerated" timestamps for the two cache variants
   *  so the dialog can display them next to each download option. */
  const fetchTemplateCacheMeta = async (cid) => {
    if (!cid) return;
    try {
      const res = await axios.get(
        `${API}/competitions/${cid}/entry-template/cache-meta`,
        { withCredentials: true }
      );
      setTemplateCacheMeta({
        blank: res.data?.blank?.updated_at || null,
        populated: res.data?.populated?.updated_at || null,
      });
    } catch (err) {
      // Non-fatal — the label just falls back to "Not yet generated".
      console.warn('cache-meta fetch failed:', err);
      setTemplateCacheMeta({ blank: null, populated: null });
    }
  };

  /** Pull the list of clubs that have at least one entry in the
   *  selected competition, used by the "Current entry list" club
   *  filter dropdown. */
  const fetchTemplateClubsWithEntries = async (cid) => {
    if (!cid) {
      setTemplateClubsWithEntries([]);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/documents/clubs-with-entries/${cid}`,
        { withCredentials: true }
      );
      setTemplateClubsWithEntries(res.data?.clubs || []);
    } catch (err) {
      console.warn('clubs-with-entries fetch failed:', err);
      setTemplateClubsWithEntries([]);
    }
  };

  // Download entry template for a competition.
  // ``populated`` = true asks the server to write existing entries
  // into the workbook (one row per skater / pair / team).
  const handleDownloadTemplate = async (populated = false) => {
    if (!templateCompetitionId) {
      toast.error('Please select a competition');
      return;
    }

    setDownloadingTemplate(true);
    try {
      // Build query params:
      //   * ?populated=true  when downloading the current entry list
      //   * ?club_filter=ID  when a specific club has been selected
      //     (only meaningful for the populated variant; the blank
      //     template is club-agnostic).
      const params = {};
      if (populated) {
        params.populated = true;
        if (templateClubFilter) {
          params.club_filter = templateClubFilter;
        }
      }
      const response = await axios.get(
        `${API}/competitions/${templateCompetitionId}/entry-template`,
        {
          withCredentials: true,
          responseType: 'blob',
          params: Object.keys(params).length ? params : undefined,
        }
      );
      
      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = populated ? 'Entry_Template_Populated.xlsx' : 'Entry_Template.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(populated ? 'Populated entry list downloaded' : 'Entry template downloaded');
      setTemplateDialogOpen(false);
      // Refresh the "last regenerated" timestamps for both variants.
      void fetchTemplateCacheMeta(templateCompetitionId);
      setTemplateCompetitionId('');
      setTemplateClubFilter('');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };
  
  // Handle entry import
  const handleImport = async () => {
    if (!importCompetitionId) {
      toast.error('Please select a competition');
      return;
    }
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API}/competitions/${importCompetitionId}/import-entries`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setImportResults(response.data);
      
      // If there are conflicts, show the conflict resolution dialog
      if (response.data.skater_conflicts?.length > 0) {
        setConflictQueue(response.data.skater_conflicts);
        setCurrentConflict(response.data.skater_conflicts[0]);
        setConflictDialogOpen(true);
      }
      
      // Refresh entries list
      await fetchEntries();
      if (fetchSkaters) {
        await fetchSkaters();
      }
      
      // Show success message
      if (response.data.success_count > 0) {
        toast.success(`Imported ${response.data.success_count} entries successfully`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors - see report`);
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
  
  // Handle conflict resolution
  const handleConflictResolution = async (action) => {
    if (!currentConflict) return;
    
    try {
      await axios.post(`${API}/entries/resolve-conflict`, {
        action: action,
        existing_skater_id: currentConflict.existing_skater?.skater_id,
        import_data: currentConflict.import_data,
        entries: currentConflict.entries,
        competition_id: importCompetitionId
      }, { withCredentials: true });
      
      toast.success('Conflict resolved');
      
      // Move to next conflict
      const remaining = conflictQueue.slice(1);
      if (remaining.length > 0) {
        setConflictQueue(remaining);
        setCurrentConflict(remaining[0]);
      } else {
        setConflictDialogOpen(false);
        setCurrentConflict(null);
        setConflictQueue([]);
        await fetchEntries();
        if (fetchSkaters) {
          await fetchSkaters();
        }
      }
    } catch (error) {
      console.error('Conflict resolution error:', error);
      toast.error(error.response?.data?.detail || 'Failed to resolve conflict');
    }
  };
  
  // Download error report
  const downloadErrorReport = () => {
    if (!importResults?.error_report_url) return;
    
    const link = document.createElement('a');
    link.href = importResults.error_report_url;
    link.download = 'Import_Errors.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  
  // Reset import dialog
  const resetImportDialog = () => {
    setImportDialogOpen(false);
    setImportCompetitionId('');
    setImportFile(null);
    setImportResults(null);
  };
  
  // Get display name for skater
  const getSkaterDisplayName = (entry) => {
    if (entry.entry_type === 'individual' && entry.skater1) {
      return `${entry.skater1.first_name} ${entry.skater1.last_name}`;
    }
    if (entry.entry_type === 'pair') {
      const s1 = entry.skater1 ? `${entry.skater1.first_name} ${entry.skater1.last_name}` : '?';
      const s2 = entry.skater2 ? `${entry.skater2.first_name} ${entry.skater2.last_name}` : '?';
      return entry.team_name || `${s1} & ${s2}`;
    }
    if (entry.entry_type === 'team') {
      return entry.team_name || `Team (${entry.team_skaters?.length || 0} skaters)`;
    }
    return '-';
  };
  
  const getEntryTypeIcon = (type) => {
    switch (type) {
      case 'individual': return <User className="h-4 w-4" />;
      case 'pair': return <Users className="h-4 w-4" />;
      case 'team': return <UsersRound className="h-4 w-4" />;
      default: return null;
    }
  };
  
  const getStatusBadge = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      withdrawn: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-stone-100'}>{status}</Badge>;
  };
  
  // Build the unique event list for the Event filter, scoped to selected competition
  const getEventOptions = () => {
    const seen = new Map();
    entries.forEach(entry => {
      if (competitionFilter !== 'all' && entry.competition_id !== competitionFilter) return;
      if (!entry.grade_id) return;
      const label = entry.discipline_name
        ? `${entry.grade_name || ''} ${entry.discipline_name}`.trim()
        : (entry.grade_name || '');
      if (!label) return;
      const existing = seen.get(entry.grade_id);
      if (existing) {
        existing.count += 1;
      } else {
        seen.set(entry.grade_id, { label, count: 1 });
      }
    });
    return Array.from(seen.entries())
      .map(([grade_id, { label, count }]) => ({ grade_id, label, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    const matchesCompetition = competitionFilter === 'all' || entry.competition_id === competitionFilter;
    const matchesEvent = eventFilter === 'all' || entry.grade_id === eventFilter;
    const matchesSkater = !skaterFilter || 
      getSkaterDisplayName(entry).toLowerCase().includes(skaterFilter.toLowerCase());
    return matchesCompetition && matchesEvent && matchesSkater;
  });
  
  // Number of discipline/grade rows based on entry type
  const numEntryRows = formData.entry_type === 'pair' ? 3 : formData.entry_type === 'team' ? 1 : 6;
  
  return (
    <div className="space-y-4">
      {/* Filters and Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 flex-wrap">
          {/* Competition Filter */}
          <Select 
            value={competitionFilter} 
            onValueChange={(value) => {
              setCompetitionFilter(value);
              setEventFilter('all'); // reset event filter when competition changes
            }}
          >
            <SelectTrigger className="w-[250px]" data-testid="entry-competition-filter">
              <SelectValue placeholder="Filter by competition" />
            </SelectTrigger>
            <SelectContent className="z-[200]">
              <SelectItem value="all">All Competitions</SelectItem>
              {competitions.map(comp => (
                <SelectItem key={comp.competition_id} value={comp.competition_id}>
                  {comp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Event Filter */}
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-[260px]" data-testid="entry-event-filter">
              <SelectValue placeholder="Filter by event" />
            </SelectTrigger>
            <SelectContent className="z-[200] max-h-[400px]">
              <SelectItem value="all">All Events</SelectItem>
              {getEventOptions().map(opt => (
                <SelectItem key={opt.grade_id} value={opt.grade_id}>
                  {opt.label} ({opt.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Skater Name Filter */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Filter by skater name..."
              value={skaterFilter}
              onChange={(e) => setSkaterFilter(e.target.value)}
              className="pl-10"
              data-testid="entry-skater-filter"
            />
            {skaterFilter && (
              <button 
                onClick={() => setSkaterFilter('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-stone-600" />
              </button>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              const cid = competitions.length === 1
                ? competitions[0].competition_id : '';
              if (cid) setTemplateCompetitionId(cid);
              setTemplatePopulated(false);
              setTemplateClubFilter('');
              setTemplateDialogOpen(true);
              if (cid) {
                void fetchTemplateCacheMeta(cid);
                void fetchTemplateClubsWithEntries(cid);
              }
            }}
            data-testid="download-template-btn"
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Entry Summaries
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            data-testid="upload-entries-btn"
            className="border-green-600 text-green-700 hover:bg-green-50"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Entries
          </Button>
          
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-green-700 hover:bg-green-800"
            data-testid="add-entry-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Entry
          </Button>
          
          {/* Delete All Entries - Super User only */}
          {isSuperUser && (
            <Button 
              variant="outline"
              onClick={openBulkDeleteDialog}
              className="border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-all-entries-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Entries
            </Button>
          )}
        </div>
      </div>
      
      {/* Entries Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="entries-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-stone-600 w-16">#</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Skater(s)</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Event</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Competition</th>
                <th className="text-center py-3 px-4 font-medium text-stone-600">Status</th>
                <th className="text-center py-3 px-4 font-medium text-stone-600 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    {entries.length === 0 ? 'No entries yet. Click "Add Entry" to create one.' : 'No entries match your filters.'}
                  </td>
                </tr>
              ) : (
                filteredEntries.map(entry => (
                  <tr key={entry.entry_id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4 text-sm text-muted-foreground">{entry.entry_number}</td>
                    <td className="py-3 px-4 font-medium">{getSkaterDisplayName(entry)}</td>
                    <td className="py-3 px-4 text-sm">
                      {entry.grade_name && entry.discipline_name 
                        ? `${entry.grade_name} ${entry.discipline_name}`
                        : entry.grade_name || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{entry.competition_short_name || '-'}</td>
                    <td className="py-3 px-4 text-center">{getStatusBadge(entry.entry_status)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => handleView(entry)} 
                          className="action-icon"
                          title="View"
                          data-testid={`view-entry-${entry.entry_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {canEditItem(entry) && (
                          <>
                            <button 
                              onClick={() => handleEdit(entry)} 
                              className="action-icon"
                              title="Edit"
                              data-testid={`edit-entry-${entry.entry_id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(entry.entry_id)} 
                              className="action-icon"
                              title="Delete"
                              data-testid={`delete-entry-${entry.entry_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={editingItem ? handleEditSubmit : handleSubmit} className="space-y-6 pt-4">
            {/* Competition and Entry Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Competition *</Label>
                <Select 
                  value={formData.competition_id} 
                  onValueChange={(value) => setFormData({...formData, competition_id: value})}
                  disabled={!!editingItem}
                >
                  <SelectTrigger data-testid="entry-competition-select">
                    <SelectValue placeholder="Select competition" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {competitions.map(comp => (
                      <SelectItem key={comp.competition_id} value={comp.competition_id}>
                        {comp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Entry Type *</Label>
                <Select 
                  value={formData.entry_type} 
                  onValueChange={(value) => setFormData({
                    ...formData, 
                    entry_type: value,
                    skater1_id: '',
                    skater2_id: '',
                    team_skater_ids: [],
                    team_reserve_ids: [],
                    team_name: '',
                    entries: formData.entries.map(() => ({ discipline_id: '', grade_id: '' }))
                  })}
                  disabled={!!editingItem}
                >
                  <SelectTrigger data-testid="entry-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="pair">Pair</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Skater Selection - Individual */}
            {formData.entry_type === 'individual' && (
              <div className="space-y-2">
                <Label>Skater *</Label>
                <Select 
                  value={formData.skater1_id} 
                  onValueChange={(value) => setFormData({...formData, skater1_id: value})}
                >
                  <SelectTrigger data-testid="entry-skater1-select">
                    <SelectValue placeholder="Select skater" />
                  </SelectTrigger>
                  <SelectContent className="z-[200] max-h-[300px]">
                    {skaters.map(skater => (
                      <SelectItem key={skater.skater_id} value={skater.skater_id}>
                        {skater.first_name} {skater.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Skater Selection - Pair */}
            {formData.entry_type === 'pair' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Skater 1 *</Label>
                    <Select 
                      value={formData.skater1_id} 
                      onValueChange={(value) => setFormData({...formData, skater1_id: value})}
                    >
                      <SelectTrigger data-testid="entry-pair-skater1">
                        <SelectValue placeholder="Select skater 1" />
                      </SelectTrigger>
                      <SelectContent className="z-[200] max-h-[300px]">
                        {skaters.filter(s => s.skater_id !== formData.skater2_id).map(skater => (
                          <SelectItem key={skater.skater_id} value={skater.skater_id}>
                            {skater.first_name} {skater.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Skater 2 *</Label>
                    <Select 
                      value={formData.skater2_id} 
                      onValueChange={(value) => setFormData({...formData, skater2_id: value})}
                    >
                      <SelectTrigger data-testid="entry-pair-skater2">
                        <SelectValue placeholder="Select skater 2" />
                      </SelectTrigger>
                      <SelectContent className="z-[200] max-h-[300px]">
                        {skaters.filter(s => s.skater_id !== formData.skater1_id).map(skater => (
                          <SelectItem key={skater.skater_id} value={skater.skater_id}>
                            {skater.first_name} {skater.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            
            {/* Skater Selection - Team */}
            {formData.entry_type === 'team' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Team Name *</Label>
                  <Input 
                    value={formData.team_name}
                    onChange={(e) => setFormData({...formData, team_name: e.target.value})}
                    placeholder="e.g., Auckland Synchro A"
                    required
                    data-testid="entry-team-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Club managing this entry</Label>
                  <Select
                    value={formData.managing_club_id || ''}
                    onValueChange={(value) => setFormData({...formData, managing_club_id: value})}
                  >
                    <SelectTrigger data-testid="entry-managing-club">
                      <SelectValue placeholder="Select managing club" />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      {clubs.map(c => (
                        <SelectItem key={c.club_id} value={c.club_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Teams can include members from different clubs — this is the
                    club responsible for paying and admin for this entry.
                  </p>
                </div>
                
                {/* Team Members - Dynamic Combo Boxes */}
                <div className="space-y-3">
                  <Label>Team Members * (minimum 4)</Label>
                  <div className="space-y-2">
                    {/* Render team member dropdowns - start with 4, add more as needed */}
                    {Array.from({ length: Math.max(4, formData.team_skater_ids.filter(id => !formData.team_reserve_ids.includes(id)).length + 1) }).map((_, index) => {
                      // Get non-reserve members only
                      const nonReserveMembers = formData.team_skater_ids.filter(id => !formData.team_reserve_ids.includes(id));
                      const currentValue = nonReserveMembers[index] || '';
                      
                      // Available skaters: not already selected (except current slot)
                      const selectedIds = new Set([...formData.team_skater_ids, ...formData.team_reserve_ids]);
                      const availableSkaters = skaters.filter(s => 
                        !selectedIds.has(s.skater_id) || s.skater_id === currentValue
                      );
                      
                      // Only show this dropdown if:
                      // - It's one of the first 4 slots, OR
                      // - All previous slots are filled
                      const shouldShow = index < 4 || nonReserveMembers.length >= index;
                      
                      if (!shouldShow) return null;
                      
                      return (
                        <div key={`member-${index}`} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                          <select
                            className="flex-1 border rounded-lg px-3 py-2 text-sm"
                            value={currentValue}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const newMembers = [...nonReserveMembers];
                              
                              if (newValue) {
                                newMembers[index] = newValue;
                              } else {
                                newMembers.splice(index, 1);
                              }
                              
                              // Combine with reserves to get full team_skater_ids
                              const allMembers = [...newMembers.filter(Boolean), ...formData.team_reserve_ids];
                              setFormData({...formData, team_skater_ids: allMembers});
                            }}
                            data-testid={`team-member-${index}`}
                          >
                            <option value="">Select skater...</option>
                            {availableSkaters.map(s => (
                              <option key={s.skater_id} value={s.skater_id}>
                                {s.first_name} {s.last_name} ({s.club_name || 'No club'})
                              </option>
                            ))}
                          </select>
                          {currentValue && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                              onClick={() => {
                                const newMembers = nonReserveMembers.filter((_, i) => i !== index);
                                const allMembers = [...newMembers, ...formData.team_reserve_ids];
                                setFormData({...formData, team_skater_ids: allMembers});
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Members selected: <strong className="text-stone-700">{formData.team_skater_ids.filter(id => !formData.team_reserve_ids.includes(id)).length}</strong>
                  </p>
                </div>
                
                {/* Team Reserves - Dynamic Combo Boxes */}
                <div className="space-y-3">
                  <Label>Team Reserves</Label>
                  <div className="space-y-2">
                    {/* Render reserve dropdowns - start with up to 4, add more as needed */}
                    {Array.from({ length: Math.max(1, Math.min(4, formData.team_reserve_ids.length + 1)) }).map((_, index) => {
                      const currentValue = formData.team_reserve_ids[index] || '';
                      
                      // Available skaters: not already selected (except current slot)
                      const selectedIds = new Set([...formData.team_skater_ids, ...formData.team_reserve_ids]);
                      const availableSkaters = skaters.filter(s => 
                        !selectedIds.has(s.skater_id) || s.skater_id === currentValue
                      );
                      
                      // Only show if it's the first slot or previous slots are filled
                      const shouldShow = index === 0 || formData.team_reserve_ids.length >= index;
                      
                      if (!shouldShow) return null;
                      
                      return (
                        <div key={`reserve-${index}`} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                          <select
                            className="flex-1 border rounded-lg px-3 py-2 text-sm border-amber-200 bg-amber-50/30"
                            value={currentValue}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const newReserves = [...formData.team_reserve_ids];
                              
                              if (newValue) {
                                newReserves[index] = newValue;
                                // Also add to team_skater_ids if not already there
                                const newMembers = formData.team_skater_ids.includes(newValue) 
                                  ? formData.team_skater_ids 
                                  : [...formData.team_skater_ids, newValue];
                                setFormData({...formData, team_skater_ids: newMembers, team_reserve_ids: newReserves.filter(Boolean)});
                              } else {
                                newReserves.splice(index, 1);
                                setFormData({...formData, team_reserve_ids: newReserves});
                              }
                            }}
                            data-testid={`team-reserve-${index}`}
                          >
                            <option value="">Select reserve...</option>
                            {availableSkaters.map(s => (
                              <option key={s.skater_id} value={s.skater_id}>
                                {s.first_name} {s.last_name} ({s.club_name || 'No club'})
                              </option>
                            ))}
                          </select>
                          {currentValue && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                              onClick={() => {
                                const newReserves = formData.team_reserve_ids.filter((_, i) => i !== index);
                                // Also remove from team_skater_ids
                                const removedId = formData.team_reserve_ids[index];
                                const newMembers = formData.team_skater_ids.filter(id => id !== removedId);
                                setFormData({...formData, team_skater_ids: newMembers, team_reserve_ids: newReserves});
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {formData.team_reserve_ids.length > 0 && (
                    <p className="text-sm text-amber-600">
                      Reserves: <strong>{formData.team_reserve_ids.length}</strong>
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Discipline/Grade Selection Rows */}
            {formData.competition_id && (
              <div className="space-y-3">
                <Label>Events to Enter</Label>
                {formData.entry_type === 'team' ? (
                  // Single grade selection for teams
                  <div className="grid grid-cols-2 gap-3">
                    <Select 
                      value={formData.entries[0].discipline_id}
                      onValueChange={(value) => updateEntryRow(0, 'discipline_id', value)}
                    >
                      <SelectTrigger data-testid="team-discipline">
                        <SelectValue placeholder="Select discipline" />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        {getFilteredDisciplines().map(d => (
                          <SelectItem key={d.discipline_id} value={d.discipline_id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={formData.entries[0].grade_id}
                      onValueChange={(value) => updateEntryRow(0, 'grade_id', value)}
                      disabled={!formData.entries[0].discipline_id}
                    >
                      <SelectTrigger data-testid="team-grade">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        {getGradesForDiscipline(formData.entries[0].discipline_id).map(sg => (
                          <SelectItem key={sg.grade_id} value={sg.grade_id}>
                            {sg.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  // Multiple discipline/grade rows for individual/pair.
                  // When editing, every existing entry shows up here with
                  // a red ✕ next to it so the organiser can remove an
                  // event without leaving the dialog.
                  <div className="space-y-2 border rounded-lg p-3">
                    {(editingItem ? formData.entries : formData.entries.slice(0, numEntryRows)).map((entry, index) => (
                      <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
                        <Select
                          value={entry.discipline_id}
                          onValueChange={(value) => updateEntryRow(index, 'discipline_id', value)}
                        >
                          <SelectTrigger data-testid={`entry-discipline-${index}`}>
                            <SelectValue placeholder={`Discipline ${index + 1}`} />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            {getFilteredDisciplines().map(d => (
                              <SelectItem key={d.discipline_id} value={d.discipline_id}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={entry.grade_id}
                          onValueChange={(value) => updateEntryRow(index, 'grade_id', value)}
                          disabled={!entry.discipline_id}
                        >
                          <SelectTrigger data-testid={`entry-grade-${index}`}>
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent className="z-[200]">
                            {getGradesForDiscipline(entry.discipline_id).map(sg => (
                              <SelectItem key={sg.grade_id} value={sg.grade_id}>
                                {sg.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {editingItem ? (
                          <button
                            type="button"
                            onClick={() => removeEntryRow(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title={entry.entry_id ? 'Remove this entry' : 'Clear this row'}
                            data-testid={`remove-entry-row-${index}`}
                          >
                            <X className="h-5 w-5" />
                          </button>
                        ) : (
                          <span className="w-7" />
                        )}
                      </div>
                    ))}
                    {editingItem && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-1"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          entries: [...prev.entries, { discipline_id: '', grade_id: '' }],
                        }))}
                        data-testid="add-entry-row-btn"
                      >
                        <Plus className="mr-1.5 h-4 w-4" />
                        Add another event
                      </Button>
                    )}
                  </div>
                )}
                {!editingItem && formData.entry_type !== 'team' && (
                  <p className="text-sm text-muted-foreground">
                    Fill in one or more rows. Empty rows will be skipped.
                  </p>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-green-700 hover:bg-green-800" 
                data-testid="save-entry-btn"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : editingItem ? 'Update Entry' : 'Create Entries'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Entry Details</DialogTitle>
          </DialogHeader>
          
          {viewingItem && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Entry #</span>
                <span className="font-bold text-lg">{viewingItem.entry_number}</span>
                {getStatusBadge(viewingItem.entry_status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Entry Type</p>
                  <p className="font-medium capitalize flex items-center gap-2">
                    {getEntryTypeIcon(viewingItem.entry_type)}
                    {viewingItem.entry_type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Music Submitted</p>
                  <p className="font-medium">{viewingItem.music_submitted ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Event</p>
                <p className="font-medium">
                  {viewingItem.grade_name && viewingItem.discipline_name 
                    ? `${viewingItem.grade_name} ${viewingItem.discipline_name}`
                    : viewingItem.grade_name || '-'}
                </p>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Skater(s)</p>
                {viewingItem.entry_type === 'individual' && viewingItem.skater1 && (
                  <p className="font-medium">
                    {viewingItem.skater1.first_name} {viewingItem.skater1.last_name}
                    <span className="text-sm text-muted-foreground ml-2">
                      ({viewingItem.skater1.club_name || 'No club'})
                    </span>
                  </p>
                )}
                {viewingItem.entry_type === 'pair' && (
                  <div className="space-y-1">
                    {viewingItem.team_name && (
                      <p className="font-semibold text-green-800">{viewingItem.team_name}</p>
                    )}
                    <p className="font-medium">
                      {viewingItem.skater1?.first_name} {viewingItem.skater1?.last_name}
                      <span className="text-sm text-muted-foreground ml-2">
                        ({viewingItem.skater1?.club_name || 'No club'})
                      </span>
                    </p>
                    <p className="font-medium">
                      {viewingItem.skater2?.first_name} {viewingItem.skater2?.last_name}
                      <span className="text-sm text-muted-foreground ml-2">
                        ({viewingItem.skater2?.club_name || 'No club'})
                      </span>
                    </p>
                  </div>
                )}
                {viewingItem.entry_type === 'team' && (
                  <div className="space-y-1">
                    <p className="font-semibold text-green-800">{viewingItem.team_name}</p>
                    {viewingItem.team_skaters?.map(skater => {
                      const isReserve = viewingItem.team_reserve_ids?.includes(skater.skater_id);
                      return (
                        <p key={skater.skater_id} className="text-sm flex items-center gap-2">
                          <span>{skater.first_name} {skater.last_name}</span>
                          <span className="text-muted-foreground">
                            ({skater.club_name || 'No club'})
                          </span>
                          {isReserve && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Reserve</Badge>
                          )}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Entry"
        description="Are you sure you want to delete this entry? This action cannot be undone."
        loading={deleting}
      />
      
      {/* Entry Template Download Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-700" />
              Download Entry Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Download an Excel template to collect entries for a competition. 
              The template includes separate worksheets for Individual, Pairs, and Team entries.
            </p>
            
            <div className="space-y-2">
              <Label>Select Competition *</Label>
              <Select
                value={templateCompetitionId}
                onValueChange={(v) => {
                  setTemplateCompetitionId(v);
                  setTemplateClubFilter('');  // reset to All Clubs on comp change
                  void fetchTemplateCacheMeta(v);
                  void fetchTemplateClubsWithEntries(v);
                }}
              >
                <SelectTrigger data-testid="template-competition-select" className="w-full">
                  <SelectValue placeholder="Choose a competition" />
                </SelectTrigger>
                <SelectContent className="z-[200] max-w-[450px]">
                  {competitions.map(comp => (
                    <SelectItem key={comp.competition_id} value={comp.competition_id}>
                      {comp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Variant toggle — "Blank template" vs "Populated with
                current entries". The two share the same workbook
                layout; the populated variant simply fills in rows. */}
            <div className="space-y-2">
              <Label>Download Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTemplatePopulated(false)}
                  data-testid="template-variant-blank"
                  className={`text-left rounded-lg border-2 p-3 transition ${
                    !templatePopulated
                      ? 'border-green-700 bg-green-50'
                      : 'border-stone-200 bg-white hover:bg-stone-50'
                  }`}
                >
                  <p className="text-sm font-medium">Blank template</p>
                  <p className="text-[11px] text-stone-500 mt-1">
                    Empty form for clubs to fill in.
                  </p>
                  <p className="text-[10px] text-stone-400 mt-1.5">
                    Last regenerated:{' '}
                    {templateCacheMeta.blank
                      ? new Date(templateCacheMeta.blank).toLocaleString('en-NZ',
                          { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Not yet generated'}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplatePopulated(true)}
                  data-testid="template-variant-populated"
                  className={`text-left rounded-lg border-2 p-3 transition ${
                    templatePopulated
                      ? 'border-blue-700 bg-blue-50'
                      : 'border-stone-200 bg-white hover:bg-stone-50'
                  }`}
                >
                  <p className="text-sm font-medium">Current entry list</p>
                  <p className="text-[11px] text-stone-500 mt-1">
                    Populated with this competition's entries.
                  </p>
                  <p className="text-[10px] text-stone-400 mt-1.5">
                    Last regenerated:{' '}
                    {templateCacheMeta.populated
                      ? new Date(templateCacheMeta.populated).toLocaleString('en-NZ',
                          { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Not yet generated'}
                  </p>
                </button>
              </div>
            </div>

            {/* Club filter — only meaningful for the populated variant.
                "All Clubs" is the default; selecting a specific club
                restricts the populated workbook to that club's
                Individual entries, Pairs where either skater belongs
                to the club, and Teams where it is the managing club. */}
            {templatePopulated && (
              <div className="space-y-2" data-testid="template-club-filter-wrap">
                <Label>Filter by Club</Label>
                <Select
                  value={templateClubFilter || '__all__'}
                  onValueChange={(v) => setTemplateClubFilter(v === '__all__' ? '' : v)}
                >
                  <SelectTrigger
                    data-testid="template-club-filter-select"
                    className="w-full"
                  >
                    <SelectValue placeholder="All Clubs" />
                  </SelectTrigger>
                  <SelectContent className="z-[200] max-w-[450px]">
                    <SelectItem value="__all__">All Clubs</SelectItem>
                    {templateClubsWithEntries.map((c) => (
                      <SelectItem key={c.club_id} value={c.club_id}>
                        {c.club_name}
                        {c.entry_count
                          ? ` (${c.entry_count} ${c.entry_count === 1 ? 'entry' : 'entries'})`
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-stone-500">
                  Pairs are included for both clubs; Teams appear only under their managing club.
                </p>
              </div>
            )}

            <div className="text-xs text-muted-foreground bg-stone-50 p-3 rounded-lg space-y-1">
              <p><strong>The template includes:</strong></p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Instructions sheet with column guidelines</li>
                <li>Individual Entries worksheet</li>
                <li>Pairs Entries worksheet (both skaters per row)</li>
                <li>Team Entries worksheet (with reserve marking & Managing Club)</li>
                <li>Dropdown menus for events, clubs, and genders</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t mt-4">
              <Button
                variant="outline"
                onClick={() => { setTemplateDialogOpen(false); setTemplateCompetitionId(''); }}
                className="min-w-[80px]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDownloadTemplate(templatePopulated)}
                className={`min-w-[160px] ${templatePopulated
                  ? 'bg-blue-700 hover:bg-blue-800'
                  : 'bg-green-700 hover:bg-green-800'}`}
                disabled={!templateCompetitionId || downloadingTemplate}
                data-testid="download-template-confirm-btn"
              >
                {downloadingTemplate ? (
                  <>Generating...</>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    {templatePopulated ? 'Download Entry List' : 'Download Template'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Import Entries Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-700" />
              Upload Entries
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {!importResults ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Before uploading:</p>
                      <ul className="text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                        <li>Rows missing required data will be skipped</li>
                        <li>Duplicate entries will be reported but not created</li>
                        <li>Team entries fail entirely if any member has errors</li>
                        <li>You can download an error report after import</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Select Competition *</Label>
                  <Select 
                    value={importCompetitionId} 
                    onValueChange={setImportCompetitionId}
                  >
                    <SelectTrigger data-testid="import-competition-select" className="w-full">
                      <SelectValue placeholder="Choose a competition" />
                    </SelectTrigger>
                    <SelectContent className="z-[200] max-w-[450px]">
                      {competitions.map(comp => (
                        <SelectItem key={comp.competition_id} value={comp.competition_id}>
                          {comp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Closing date warning for standard users */}
                {importCompetitionId && !canUploadEntries(importCompetitionId) && (
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm">
                    <div className="flex gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                      <div className="text-amber-800">
                        <p className="font-medium">Closing date has passed</p>
                        <p>We've passed the closing date for entries so bulk uploads are no longer available. Please contact the competition organiser if you need to make changes.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Entry File (Excel) *</Label>
                  <Input 
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="import-file-input"
                  />
                  {importFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 pt-2 border-t mt-4">
                  <Button 
                    variant="outline" 
                    onClick={resetImportDialog}
                    className="min-w-[80px]"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImport}
                    className="bg-green-700 hover:bg-green-800 min-w-[140px]"
                    disabled={!importCompetitionId || !importFile || importing || !canUploadEntries(importCompetitionId)}
                    data-testid="import-confirm-btn"
                  >
                    {importing ? (
                      <>Importing...</>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Import Results */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-700">{importResults.success_count}</p>
                      <p className="text-sm text-green-600">Entries Created</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-red-700">{importResults.error_count}</p>
                      <p className="text-sm text-red-600">Errors</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>New Skaters:</span>
                      <span className="font-medium">{importResults.created_skaters}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>Updated Skaters:</span>
                      <span className="font-medium">{importResults.updated_skaters}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>Duplicates Skipped:</span>
                      <span className="font-medium">{importResults.duplicate_count}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>Conflicts to Resolve:</span>
                      <span className="font-medium">{importResults.skater_conflicts?.length || 0}</span>
                    </div>
                  </div>
                  
                  {importResults.error_report_url && (
                    <Button 
                      variant="outline" 
                      onClick={downloadErrorReport}
                      className="w-full border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Error Report
                    </Button>
                  )}
                  
                  <div className="flex justify-end pt-2 border-t">
                    <Button onClick={resetImportDialog}>
                      Done
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Conflict Resolution Dialog */}
      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {currentConflict?.match_type === 'fuzzy'
                ? 'Possible Duplicate (Name Typo?)'
                : 'Skater Name Conflict'}
            </DialogTitle>
          </DialogHeader>

          {currentConflict && (
            <div className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                {currentConflict.match_type === 'fuzzy' ? (
                  <>
                    A skater with a <strong>very similar name</strong> is
                    already in the registry — likely a typo on this import
                    row. Match confidence:{' '}
                    <strong>
                      {Math.round((currentConflict.fuzzy_score || 0) * 100)}%
                    </strong>. How would you like to proceed?
                  </>
                ) : (
                  'A skater with this name already exists but with a different club. How would you like to proceed?'
                )}
              </p>

              <div className="grid gap-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium mb-1">FROM IMPORT FILE:</p>
                  <p className="font-medium">{currentConflict.import_data?.first_name} {currentConflict.import_data?.last_name}</p>
                  <p className="text-sm text-muted-foreground">Club: {currentConflict.import_data?.club_name || 'Not specified'}</p>
                </div>

                <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg">
                  <p className="text-xs text-stone-600 font-medium mb-1">EXISTING SKATER:</p>
                  <p className="font-medium">{currentConflict.existing_skater?.first_name} {currentConflict.existing_skater?.last_name}</p>
                  <p className="text-sm text-muted-foreground">Club: {currentConflict.existing_skater?.club_name || 'Not specified'}</p>
                </div>
              </div>
              
              <div className="text-xs text-muted-foreground">
                Remaining conflicts: {conflictQueue.length - 1}
              </div>
              
              <div className="flex flex-col gap-2 pt-2 border-t">
                <Button 
                  onClick={() => handleConflictResolution('use_existing')}
                  variant="outline"
                  className="justify-start"
                >
                  Use existing skater (keep their club)
                </Button>
                <Button 
                  onClick={() => handleConflictResolution('update_existing')}
                  variant="outline"
                  className="justify-start"
                >
                  Update existing skater (use imported club)
                </Button>
                <Button 
                  onClick={() => handleConflictResolution('create_new')}
                  className="bg-green-700 hover:bg-green-800 justify-start"
                >
                  Create new skater record
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Dialog - Competition Selection */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Entries
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-red-800">
                  <p className="font-medium">Warning</p>
                  <p>This action will permanently delete entries and cannot be undone.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Select Competition</Label>
              <Select value={bulkDeleteCompetitionId} onValueChange={setBulkDeleteCompetitionId}>
                <SelectTrigger data-testid="bulk-delete-competition-select" className="w-full">
                  <SelectValue placeholder="Select competition..." />
                </SelectTrigger>
                <SelectContent className="z-[200] max-w-[600px]">
                  <SelectItem value="all" className="text-red-600 font-medium">
                    Delete entries from ALL competitions ({competitionCounts.total_entries} entries)
                  </SelectItem>
                  {competitionCounts.competitions.map(comp => (
                    <SelectItem key={comp.competition_id} value={comp.competition_id}>
                      {comp.name} ({comp.entry_count} entries)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-2 border-t mt-4">
              <Button 
                variant="outline" 
                onClick={() => setBulkDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setBulkDeleteDialogOpen(false);  // Close first dialog
                  setTimeout(() => setBulkDeleteConfirmOpen(true), 150);  // Then show confirmation
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={getBulkDeleteEntryCount() === 0}
                data-testid="bulk-delete-continue-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Final Confirmation */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Final Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              This action cannot be undone. All {getBulkDeleteEntryCount()} entries for{' '}
              <span className="font-semibold text-stone-800">{getBulkDeleteTargetName()}</span>{' '}
              will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? 'Deleting...' : 'Yes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
